import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useCallback, useState } from 'react';
import { useOrganisationFilter } from '@/features/organisations/providers/organisationHooks';
import { useDeleteTest, useUpdateTest } from '@/features/tests/hooks/tests';
import { useNotifications } from '@/providers/notificationHooks';
import { useApiErrorMessage } from '@/shared/data/errors';
import { updateTagColor, type TagColor } from '@/shared/data/tags';
import { queryKeys } from '@/shared/hooks';
import type { TestListItemDto } from '@flowqa/shared';

type DeleteTarget = {
  id: string;
  name: string;
  projectId: string;
};

type UseTestsCloneRowActionsParams = {
  resolvedToken: string | null;
  selectedProjectId: string | null;
};

type UseTestsCloneRowActionsResult = {
  deleteTarget: DeleteTarget | null;
  deleteLoading: boolean;
  deleteRowPendingId: string | null;
  updateRowTagsColorPendingId: string | null;
  onDeleteRow: (row: TestListItemDto) => void;
  onDeleteClose: () => void;
  onDeleteConfirm: () => void;
  onUpdateRowTagsColor: (input: {
    row: TestListItemDto;
    tags: string[];
    colors: TagColor[];
  }) => void;
};

export const useTestsTableRowActions = ({
  resolvedToken,
  selectedProjectId,
}: UseTestsCloneRowActionsParams): UseTestsCloneRowActionsResult => {
  const queryClient = useQueryClient();
  const { selectedOrganisationId } = useOrganisationFilter();
  const { notify } = useNotifications();
  const toErrorMessage = useApiErrorMessage();
  const deleteTestMutation = useDeleteTest();
  const updateTestMutation = useUpdateTest();
  const [updatingTagRowId, setUpdatingTagRowId] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<DeleteTarget | null>(null);

  const updateTagColorMutation = useMutation({
    mutationFn: async (input: { tags: string[]; colors: TagColor[] }) => {
      const updates = input.tags
        .map((name, index) => ({ name: name.trim(), color: input.colors[index] ?? 'neutral' }))
        .filter((item) => item.name.length > 0);
      if (updates.length === 0) return;
      await Promise.all(
        updates.map(async (item) => {
          await updateTagColor(
            item,
            resolvedToken ?? undefined,
            selectedOrganisationId ?? undefined,
          );
        }),
      );
    },
  });

  const onDeleteRow = useCallback((row: TestListItemDto) => {
    setDeleteTarget({ id: row.id, name: row.name, projectId: row.projectId });
  }, []);

  const onDeleteClose = useCallback(() => {
    if (deleteTestMutation.isPending) return;
    setDeleteTarget(null);
  }, [deleteTestMutation.isPending]);

  const onDeleteConfirm = useCallback(() => {
    if (!deleteTarget) return;
    const deletedTestName = deleteTarget.name;
    deleteTestMutation.mutate(
      { id: deleteTarget.id, projectId: deleteTarget.projectId },
      {
        onSuccess: () => {
          notify({ message: `Test '${deletedTestName}' deleted.`, tone: 'green' });
          setDeleteTarget(null);
        },
        onError: (error) => {
          notify({ message: toErrorMessage(error), tone: 'red' });
        },
      },
    );
  }, [deleteTarget, deleteTestMutation, notify, toErrorMessage]);

  const onUpdateRowTagsColor = useCallback(
    ({ row, tags, colors }: { row: TestListItemDto; tags: string[]; colors: TagColor[] }) => {
      const normalizedTags = Array.from(
        new Set(tags.map((tag) => tag.trim()).filter((tag) => tag.length > 0)),
      );
      const normalizedColors = colors.slice(0, normalizedTags.length);
      setUpdatingTagRowId(row.id);
      void (async () => {
        try {
          await updateTestMutation.mutateAsync({ id: row.id, tags: normalizedTags });
          if (normalizedTags.length > 0) {
            await updateTagColorMutation.mutateAsync({
              tags: normalizedTags,
              colors: normalizedColors,
            });
          }
          notify({ message: 'Tags updated.', tone: 'green' });
          await queryClient.invalidateQueries({
            queryKey: queryKeys.tagOptions(selectedProjectId, selectedOrganisationId ?? null),
          });
        } catch (error) {
          notify({ message: toErrorMessage(error), tone: 'red' });
        } finally {
          setUpdatingTagRowId(null);
        }
      })();
    },
    [
      notify,
      queryClient,
      selectedOrganisationId,
      selectedProjectId,
      toErrorMessage,
      updateTagColorMutation,
      updateTestMutation,
    ],
  );

  return {
    deleteTarget,
    deleteLoading: deleteTestMutation.isPending,
    deleteRowPendingId: deleteTestMutation.isPending ? (deleteTarget?.id ?? null) : null,
    updateRowTagsColorPendingId:
      updateTagColorMutation.isPending || updateTestMutation.isPending ? updatingTagRowId : null,
    onDeleteRow,
    onDeleteClose,
    onDeleteConfirm,
    onUpdateRowTagsColor,
  };
};
